import * as Sentry from "@sentry/react";
import { updateProfile } from "firebase/auth";
import { request } from "hooks/useOkoApi/useOkoApi";
import { logError, LogCategory, logSession } from "utils/sentryLogger";
import { constants } from "utils/general";
import { clearSessionState } from "utils/sessionPersistence";
import { isActingAsAnotherTeacher, impersonationBlockError } from "./impersonationBlock";

export const ONBOARDING_STEP = {
  PROFILE_SETUP: "profile_setup",
  SESSION_JOIN: "session_join",
  DEVICE_CHECK: "device_check",
  CONNECT: "connect",
};

export const ONBOARDING_SCREEN = {
  DEVICE_CHECK: "device_check",
};

export const API_TIMEOUT_MS = 30000;

export const SESSION_STATUS = {
  NOT_STARTED: "not_started",
  IN_PROGRESS: "in_progress",
  COMPLETE: "complete",
};

export const REDIRECT_ERROR = {
  RETRY_FAILURE: "retry_failure",
  SESSION_NOT_FOUND: "session_not_found",
  SESSION_INIT_FAILED: "session_init_failed",
};

/**
 * Reads navigator.connection info (Network Information API).
 * Returns null values when the API is unavailable.
 */
export const getNetworkInfo = () => {
  const conn = navigator.connection;
  return {
    networkType: conn?.effectiveType || null,
    networkRtt: conn?.rtt ?? null,
    networkDownlink: conn?.downlink ?? null,
  };
};

/**
 * Redirects to group code entry screen.
 *
 * @param {string} groupCode
 * @param {string} [errorType]    - REDIRECT_ERROR value; shown as a banner on arrival
 * @param {Object} [context]      - Optional diagnostic context passed via URL so
 *                                   GroupCode can log a proper error event on arrival.
 * @param {string} [context.source]         - Which code path caused the redirect
 *                                             (e.g. "prefetch", "vote_to_start",
 *                                             "rehydration", "ready_room_mount")
 * @param {string} [context.sessionId]      - Session id we were working with, if any
 * @param {string} [context.sessionStatus]  - Session status last observed (not_started / complete / ...)
 * @param {number} [context.statusCode]     - HTTP status of the failing request, if any
 *
 * We attempt a short Sentry flush before navigation so the detailed source-side
 * error log (stack trace, full context) has a chance to leave the browser before
 * the SDK is torn down by the page change. Navigation is guaranteed either way.
 */
export const redirectToGroupCode = (groupCode, errorType, context = {}) => {
  clearSessionState();
  const params = new URLSearchParams();
  if (errorType) params.set("error", errorType);
  if (context.source) params.set("error_source", context.source);
  if (context.sessionId) params.set("error_session_id", context.sessionId);
  if (context.sessionStatus) params.set("error_session_status", context.sessionStatus);
  if (context.statusCode != null) params.set("error_status_code", String(context.statusCode));
  const query = params.toString();
  const href = query ? `/?${query}` : "/";

  const navigate = () => {
    window.location.href = href;
  };

  if (typeof Sentry?.flush === "function") {
    Sentry.flush(1500).then(navigate, navigate);
  } else {
    navigate();
  }
};

/**
 * Creates an AbortSignal that aborts after the given timeout.
 * Uses AbortSignal.timeout() when available, falls back to AbortController + setTimeout.
 */
export const createTimeoutSignal = (ms) => {
  if (typeof AbortSignal.timeout === "function") {
    return AbortSignal.timeout(ms);
  }
  const controller = new AbortController();
  setTimeout(() => controller.abort(), ms);
  return controller.signal;
};

/**
 * Sets up the user's Firebase profile and Firestore user document.
 * Non-blocking — errors are caught and logged individually without throwing.
 */
export const setupUserProfile = async ({
  sessionAuthType,
  currentUser,
  displayName,
  authUserData,
  groupCode,
  updateUser,
}) => {
  const displayNameToSet = displayName || authUserData.displayName;
  const role = constants.STUDENT;

  // Step 1: Update Firebase profile (anonymous users only)
  if (sessionAuthType === constants.ANONYMOUS && currentUser.isAnonymous) {
    try {
      await updateProfile(currentUser, { displayName: displayNameToSet });
    } catch (e) {
      logError("Failed to update Firebase displayName", e, {
        category: LogCategory.AUTH,
        sessionAuthType,
        groupCode,
        onboardingStep: ONBOARDING_STEP.PROFILE_SETUP,
        screen: ONBOARDING_SCREEN.DEVICE_CHECK,
      });
    }
  }

  // Step 2: Update Firestore user document (timeout prevents hanging if Firestore is unreachable)
  try {
    await Promise.race([
      updateUser({ displayNameToSet, role }),
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error("Profile update timed out")), 5000)
      ),
    ]);
  } catch (e) {
    logError("Failed to update user profile", e, {
      category: LogCategory.SESSION,
      groupCode,
      userId: authUserData?.id,
      okoUserId: undefined,
      onboardingStep: ONBOARDING_STEP.PROFILE_SETUP,
      screen: ONBOARDING_SCREEN.DEVICE_CHECK,
    });
  }

  logSession("User profile setup completed", {
    groupCode,
    userId: authUserData?.id,
    okoUserId: undefined,
    onboardingStep: ONBOARDING_STEP.PROFILE_SETUP,
    screen: ONBOARDING_SCREEN.DEVICE_CHECK,
  });

  return { displayNameToSet, role };
};

/**
 * Calls the join API and fetches session metadata.
 * Logs each step individually. Throws on error (caller handles SESSION_FULL, etc.).
 */
export const joinAndFetchSession = async ({
  groupCode,
  displayName,
  clientInfo,
  authUserData,
}) => {
  if (isActingAsAnotherTeacher()) throw impersonationBlockError();

  const displayNameToSet = displayName || authUserData.displayName;

  // Step 3: Join API call
  const joinEndpoint = `group/${groupCode}/join/`;
  const groupResponse = await request(
    `${joinEndpoint}?display_name=${displayNameToSet}`,
    { method: "POST", payload: clientInfo, signal: createTimeoutSignal(API_TIMEOUT_MS) }
  );

  logSession("Join API completed", {
    sessionId: groupResponse.session_id,
    groupCode,
    userId: authUserData?.id,
    okoUserId: undefined,
    onboardingStep: ONBOARDING_STEP.SESSION_JOIN,
    screen: ONBOARDING_SCREEN.DEVICE_CHECK,
    endpoint: joinEndpoint,
  });

  // Step 4: Fetch session metadata
  const sessionEndpoint = "session/";
  let sessionResponse;
  try {
    sessionResponse = await request(
      `${sessionEndpoint}?session_id=${groupResponse.session_id}`,
      { method: "GET", signal: createTimeoutSignal(API_TIMEOUT_MS) }
    );
  } catch (e) {
    // 404 means session expired from Redis — treat as closed
    if (e?.status === 404) {
      const error = new Error("Session not found");
      error.detail = { error_code: "SESSION_CLOSED", message: "This session is no longer available." };
      logError("Session not found", error, {
        category: LogCategory.SESSION,
        sessionId: groupResponse.session_id,
        groupCode,
        userId: authUserData?.id,
        okoUserId: undefined,
        onboardingStep: ONBOARDING_STEP.SESSION_JOIN,
        screen: ONBOARDING_SCREEN.DEVICE_CHECK,
      });
      throw error;
    }
    throw e;
  }

  logSession("Session metadata fetched", {
    sessionId: groupResponse.session_id,
    groupCode,
    userId: authUserData?.id,
    okoUserId: undefined,
    activityType: sessionResponse.activity_type,
    onboardingStep: ONBOARDING_STEP.SESSION_JOIN,
    screen: ONBOARDING_SCREEN.DEVICE_CHECK,
    endpoint: sessionEndpoint,
  });

  // Validate session status — block if closed/complete
  if (sessionResponse.status === SESSION_STATUS.COMPLETE) {
    const error = new Error("Session is no longer active");
    error.detail = { error_code: "SESSION_CLOSED", message: "This session has ended." };
    logError("Session is closed", error, {
      category: LogCategory.SESSION,
      sessionId: groupResponse.session_id,
      groupCode,
      userId: authUserData?.id,
      okoUserId: undefined,
      sessionStatus: sessionResponse.status,
      onboardingStep: ONBOARDING_STEP.SESSION_JOIN,
      screen: ONBOARDING_SCREEN.DEVICE_CHECK,
    });
    throw error;
  }

  return { groupResponse, sessionResponse };
};

/**
 * Calls the vote_to_start API endpoint.
 * Returns { result, shouldRedirect } where:
 *  - result: the API response on success, null on error
 *  - shouldRedirect: true if 404 (session expired), false otherwise
 * Logs all errors to Sentry.
 */
export const voteToStart = async ({ sessionId, groupCode, userId, okoUserId }) => {
  if (!sessionId) {
    logError("vote_to_start called without sessionId", new Error("Missing sessionId"), {
      category: LogCategory.SESSION,
      groupCode,
      userId,
      okoUserId,
    });
    return { result: null, shouldRedirect: true, redirectError: REDIRECT_ERROR.SESSION_NOT_FOUND };
  }

  try {
    const res = await request(`session/${sessionId}/vote_to_start`, {
      signal: createTimeoutSignal(API_TIMEOUT_MS),
    });
    return { result: res, shouldRedirect: false };
  } catch (e) {
    if (e?.status === 404) {
      logError("vote_to_start session not found", new Error("Session expired from backend"), {
        category: LogCategory.SESSION,
        sessionId,
        groupCode,
        userId,
        okoUserId,
      });
      return { result: null, shouldRedirect: true, redirectError: REDIRECT_ERROR.SESSION_NOT_FOUND };
    }
    if (e?.name === "AbortError" || e?.name === "TimeoutError") {
      logError("vote_to_start timed out", e, {
        category: LogCategory.SESSION,
        sessionId,
        groupCode,
        userId,
        okoUserId,
      });
      return { result: null, shouldRedirect: true, redirectError: REDIRECT_ERROR.RETRY_FAILURE };
    }
    // Any other 4xx — unrecoverable client error, redirect
    if (e?.status >= 400 && e?.status < 500) {
      logError("vote_to_start client error", e, {
        category: LogCategory.SESSION,
        sessionId,
        groupCode,
        userId,
        okoUserId,
        statusCode: e.status,
      });
      return { result: null, shouldRedirect: true, redirectError: REDIRECT_ERROR.SESSION_NOT_FOUND };
    }
    // 5xx — server error, retries exhausted, redirect
    if (e?.status >= 500) {
      logError("vote_to_start server error (retries exhausted)", e, {
        category: LogCategory.SESSION,
        sessionId,
        groupCode,
        userId,
        okoUserId,
      });
      return { result: null, shouldRedirect: true, redirectError: REDIRECT_ERROR.RETRY_FAILURE };
    }
    // Network error (no status) — transient, allow retry
    logError("vote_to_start network error", e, {
      category: LogCategory.SESSION,
      sessionId,
      groupCode,
      userId,
      okoUserId,
    });
    return { result: null, shouldRedirect: false };
  }
};

/**
 * Checks the current session status via the session GET endpoint.
 * Returns { isActive, redirectError } where:
 *  - isActive: true if the session is active (not_started or in_progress)
 *  - redirectError: the REDIRECT_ERROR type when isActive is false
 */
export const checkSessionStatus = async ({ sessionId, groupCode, userId, okoUserId, source }) => {
  if (!sessionId) {
    logError("checkSessionStatus called without sessionId", new Error("Missing sessionId after join"), {
      category: LogCategory.SESSION,
      groupCode,
      userId,
      okoUserId,
      source,
    });
    return { isActive: false, redirectError: REDIRECT_ERROR.SESSION_NOT_FOUND };
  }

  try {
    const response = await request(
      `session/?session_id=${sessionId}`,
      { method: "GET" }
    );

    if (response.status === SESSION_STATUS.COMPLETE) {
      logError("Session is closed", new Error("Session is no longer active"), {
        category: LogCategory.SESSION,
        sessionId,
        groupCode,
        userId,
        okoUserId,
        sessionStatus: response.status,
        source,
      });
      return { isActive: false, redirectError: REDIRECT_ERROR.SESSION_NOT_FOUND };
    }

    return { isActive: true };
  } catch (e) {
    // 404 means session expired from Redis — treat as closed
    if (e?.status === 404) {
      logError("Session not found", new Error("Session not found in backend"), {
        category: LogCategory.SESSION,
        sessionId,
        groupCode,
        userId,
        okoUserId,
        source,
      });
      return { isActive: false, redirectError: REDIRECT_ERROR.SESSION_NOT_FOUND };
    }
    // Network / 5xx — retries already exhausted by fetchWithRetry, redirect
    logError("Failed to check session status (retries exhausted)", e, {
      category: LogCategory.SESSION,
      sessionId,
      groupCode,
      userId,
      okoUserId,
      source,
      statusCode: e?.status,
      serverResponse: e?.detail,
      errorMessage: e?.message,
    });
    return { isActive: false, redirectError: REDIRECT_ERROR.RETRY_FAILURE };
  }
};

/**
 * Builds the Firestore session document seeded when a student connects.
 * `serverTimestamp` is injected (not imported) so this stays pure and testable.
 */
export const buildSessionDocData = ({ groupResponse, sessionResponse, serverTimestamp }) => ({
  id: groupResponse.session_id,
  sessionId: groupResponse.session_id,
  okoSessionId: groupResponse.session_id,
  creatorId: "server",
  auth: groupResponse,
  updatedAt: serverTimestamp(),
  autoStart: true,
  activityId: "ready-room",
  sessionActivityType: sessionResponse.activity_type,
});

/**
 * Creates the Firestore session document only if it doesn't already exist, so a
 * late-joining student can't overwrite `activityId` back to "ready-room" on a
 * session that's already in progress.
 *
 * `getDoc` uses a snapshot listener internally and may throw if the Firestore
 * WebChannel isn't established yet (offline, E2E); in that case we fall back to
 * a setDoc-with-merge. Firestore primitives are injected for testability.
 *
 * @returns {Promise<"created"|"exists"|"created_after_read_error">}
 */
export const ensureSessionDocExists = async ({ docRef, data, getDoc, setDoc }) => {
  try {
    const docSnap = await getDoc(docRef);
    if (!docSnap.data()) {
      await setDoc(docRef, data, { merge: true });
      return "created";
    }
    return "exists";
  } catch (_firestoreReadError) {
    await setDoc(docRef, data, { merge: true });
    return "created_after_read_error";
  }
};
